import React, { useEffect, useState } from "react";
import { getDayBook } from "../../../api/endpoints/allReports";
import { authSelector } from "../../../store/selectors/index";
import { useSelector } from "react-redux";
import { message } from "antd";
import SideBar from "../../sidebar/sideBar";
import Header from "../../header/header.jsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getSiteRefrence } from "../../../api/endpoints/refrences";

// import * as XLSX from "xlsx";
// import { saveAs } from "file-saver";

const DayBook = () => {
  const { access_token } = useSelector(authSelector);
  const [dateEntered, setDateEntered] = useState("");
  const [itemsQueue, setItemsQueue] = useState([]);
  const [siteName, setSiteName] = useState();
  const [siteAddress, setSiteAddress] = useState();

  const setDate = (e) => {
    setDateEntered(e.target.value);
  };

  const getName = async (e) => {
    const result = await getSiteRefrence(access_token);
    if (result.success) {
      setSiteName(result.data.siteName);
      setSiteAddress(result.data.siteAddress);
    } else {
      message.error(result.message);
    }
  };

  const editHandler = async () => {
    if (!dateEntered) return message.error("Please type date !");
    let data = { enteredOn: dateEntered };

    const results = await getDayBook(data, access_token);
    if (!results.success) {
      return message.error(results.message || "Error fetching data");
    }

    if (results.data.length === 0) {
      return message.error("No Record Found in this date");
    }

    const payload = results.data.map((curr) => ({
      enteredOn: curr.enteredOn,
      itemCode: curr.itemCode,
      itemName: curr.itemName,
      saleRate: Number(curr.saleRate),
      purchaseRate: Number(curr.purchaseRate),
      quantity: Number(curr.quantity),
      partyName: curr.partyName,
      partyId: Number(curr.partyId),
      generalLedgerCode: Number(curr.generalLedgerCode),
      generalLedgerDescription: curr.generalLedgerDescription,
      description: curr.description,
      purchaseVoucherNo: curr.purchaseVoucherNo,
      voucherNumber: curr.voucherNumber,
      cheqNumber: curr.cheqNumber,
      debit: curr.debit,
      credit: curr.credit,
      siteId: curr.siteId,
      printOrder: curr.printOrder,
      itemDescription: curr.itemDescription,
      meterCheck: curr.meterCheck,
    }));

    setItemsQueue(payload);
    generatePDF(payload);
  };

  useEffect(() => {
    getName();
  }, []);

  function formatDate(inputDate) {
    const options = { year: "numeric", month: "long", day: "numeric" };
    const formattedDate = new Date(inputDate).toLocaleDateString(
      undefined,
      options
    );
    return formattedDate;
  }

  const generatePDF = (data) => {
    if (!data || data.length === 0) {
      message.error("No data to generate report!");
      return;
    }

    // Landscape A4
    const doc = new jsPDF("l", "mm", "a4");

    // ====== Header ======
    doc.setFontSize(10);
    doc.text(`Day Book`, 148, 5, { align: "center" });
    doc.setFontSize(16);
    doc.text(`${siteName}`, 148, 11, { align: "center" });
    doc.setFontSize(12);
    doc.text(`${siteAddress}`, 148, 16, { align: "center" });
    doc.setFontSize(10);
    doc.text(`Date: ${formatDate(dateEntered)}`, 148, 20, { align: "center" });

    // ====== Opening Balance from printOrder = 0 ======
    const openingBalance = data
      .filter((r) => r.printOrder === 0)
      .reduce(
        (sum, r) => sum + (Number(r.credit) || 0) - (Number(r.debit) || 0),
        0
      );

    // ====== Credit side (Receipts) ======
    const creditRows = data
      .filter((r) => [1, 2, 3, 6].includes(r.printOrder) && r.credit > 0)
      .map((r) => ({
        description: r.description,
        credit: r.credit,
      }));

    // 👉 Insert Opening Balance as first row in Credit side
    if (openingBalance !== 0) {
      creditRows.unshift({
        description: "Cash In Hand B/F",
        credit: openingBalance,
      });
    }

    // ====== Debit side (Payments) ======
    const debitRows = data
      .filter((r) => [15, 17, 3, 18].includes(r.printOrder) && r.debit > 0)
      .map((r) => ({
        description: r.description,
        debit: r.debit,
      }));

    // ====== Build table row by row ======
    const maxLen = Math.max(creditRows.length, debitRows.length);
    const tableRows = [];

    for (let i = 0; i < maxLen; i++) {
      const creditDesc = creditRows[i]?.description || "";
      const creditAmt = creditRows[i]
        ? Number(creditRows[i].credit || 0).toLocaleString()
        : "";

      const debitDesc = debitRows[i]?.description || "";
      const debitAmt = debitRows[i]
        ? Number(debitRows[i].debit || 0).toLocaleString()
        : "";

      tableRows.push([creditDesc, creditAmt, debitDesc, debitAmt]);
    }

    // ====== Totals ======
    // ====== Totals ======
    const totalCredit = creditRows.reduce(
      (sum, r) => sum + (parseFloat(r.credit) || 0),
      0
    );
    const totalDebit = debitRows.reduce(
      (sum, r) => sum + (parseFloat(r.debit) || 0),
      0
    );

    // Cash in Hand = Total Credit - Total Debit
    const cashInHand = totalCredit - totalDebit;

    // Push "Cash in Hand" row before totals
    tableRows.push([
      "", // empty credit description
      "", // empty credit amount
      "Cash In Hand C/F", // debit description
      { content: cashInHand.toLocaleString(), styles: { fontStyle: "bold" } },
    ]);

    // 👉 Add cashInHand into totalDebit before displaying
    const adjustedTotalDebit = totalDebit + cashInHand;

    // Push totals row
    tableRows.push([
      { content: "Total:", styles: { fontStyle: "bold" } },
      { content: totalCredit.toLocaleString(), styles: { fontStyle: "bold" } },
      { content: "Total:", styles: { fontStyle: "bold" } },
      {
        content: adjustedTotalDebit.toLocaleString(),
        styles: { fontStyle: "bold" },
      },
    ]);

    // ====== Render AutoTable ======
    autoTable(doc, {
      head: [["Description", "Receipts", "Description", "Payments"]],
      body: tableRows,
      startY: 22,
      theme: "grid",
      margin: { left: 5, right: 5 },
      styles: {
        fontSize: 9,
        cellPadding: 1,
        textColor: [0, 0, 0],
        lineColor: [0, 0, 0],
        lineWidth: 0.5,
        valign: "middle",
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        halign: "center",
        valign: "middle",
        lineColor: [0, 0, 0],
        lineWidth: 0.5,
      },
      columnStyles: {
        0: { halign: "left", cellWidth: 105, valign: "middle" },
        1: { halign: "center", cellWidth: 30, valign: "middle" },
        2: { halign: "left", cellWidth: 110, valign: "middle" },
        3: { halign: "center", cellWidth: 35, valign: "middle" },
      },
    });

    // ====== Open PDF in new tab ======
    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl);
  };

  const clearState = () => {
    setItemsQueue([]);
    setDateEntered("");
  };

  return (
    <>
      <div className="flex bg-black main">
        <SideBar />
        <div className="h-full flex flex-col content-center w-full">
          <Header />
          <div className="flex justify-center mt-32">
            <div className="meterTableMain flex flex-col w-1/2 ml-10 mt-5 mr-10">
              <div className="bg-blue-500 headingBox flex justify-center">
                <h1 className="text-white">Day Book</h1>
              </div>
              <div className="flex flex-col h-40 items-center justify-evenly">
                <div>
                  <label className="text-white">Date:</label>
                  <input
                    type="date"
                    className="text-black inpTxt p-2 h-8 ml-3 mb-3"
                    value={dateEntered}
                    onChange={setDate}
                  />
                </div>
                <div>
                  <button
                    className="bg-blue-500 hover:bg-blue-700 text-white mt-3 ml-3 py-1 px-4 rounded"
                    onClick={editHandler}
                  >
                    View PDF
                  </button>
                  <button
                    className="bg-blue-500 hover:bg-blue-700 text-white mt-3 ml-3 py-1 px-4 rounded"
                    onClick={clearState}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DayBook;
